import clsx from 'clsx'
import React, { CSSProperties, ReactNode } from 'react'
import { BasicStyledComponent, DSProductComparisonChartProduct } from 'shared-definitions/types'
import styles from './ProductComparisonChartIconsOverlay.module.css'
import AppImage from './AppImage'

export interface ProductComparisonChartIconsOverlayGroup {
  x: number
  y: number
  key: string
  products: DSProductComparisonChartProduct[]
}

interface ProductComparisonChartIconsOverlayProps extends BasicStyledComponent {
  groupedProducts: ProductComparisonChartIconsOverlayGroup[]
  onProductGroupClick: (groupProducts: DSProductComparisonChartProduct[]) => void
}

const ProductComparisonChartIconsOverlay: React.FC<ProductComparisonChartIconsOverlayProps> = ({
  className,
  groupedProducts,
  onProductGroupClick,
}) => (
  <div className={clsx(className, styles.container)}>
    {groupedProducts.map(group => {
      if (!group.products.length) {
        // No products in this group, skip rendering
        return null
      }

      const classes = clsx(styles.dotBox, styles.positionedDotBox, {
        [styles.dotBoxMultiply]: group.products.length > 1,
      })

      let content: ReactNode = null

      if (group.products.length > 1) {
        const { image } = group.products[0]
        content = (
          <>
            {image ? (
              <AppImage image={image} layout="fill" sizes={24} className={styles.dotBoxImage} />
            ) : null}
            <span className={styles.dotBoxMultiplyText}>{`+${group.products.length}`}</span>
          </>
        )
      } else if (group.products[0]?.image) {
        content = (
          <AppImage
            image={group.products[0].image}
            layout="fill"
            sizes={24}
            className={styles.dotBoxImage}
          />
        )
      }

      if (content === null) {
        return null
      }

      return (
        <button
          title={group.products.map(p => p.title).join(', ')}
          onClick={() => {
            onProductGroupClick(group.products)
          }}
          style={
            {
              '--x': group.x,
              '--y': 1 - group.y, // y = 0 is bottom, y = 1 is top
            } as CSSProperties
          }
          className={classes}
          key={group.key}
          type="button"
        >
          {content}
        </button>
      )
    })}
  </div>
)

export default ProductComparisonChartIconsOverlay
